import { ConfirmationModal } from '@shared/components';
import { useSnackbar } from '@shared/snackbar';
import { useCallback, useEffect, useMemo, useRef } from 'react';
import {
  type FieldValues,
  type SubmitHandler,
  type UseFormReturn,
  useFormContext,
} from 'react-hook-form';
import { useBeforeUnload, useBlocker, type BlockerFunction } from 'react-router-dom';

interface SubmitWithContext<TFieldValues extends FieldValues> {
  (
    formContext: UseFormReturn<TFieldValues>,
    mutationOptions?: {
      onCompleted?: () => void;
      onError?: () => void;
    },
  ): SubmitHandler<TFieldValues>;
}

type FormLeavingListenerProps<TFieldValues extends FieldValues> =
  | {
      type: 'composite';
      onSubmit: SubmitWithContext<TFieldValues>;
    }
  | {
      type?: undefined;
      onSubmit: SubmitHandler<TFieldValues>;
    };
/**
 * FormLeavingListener component.
 *
 * This component's objective is to prevent data loss by warning the user before they navigate away from the form.
 * or before they close the tab or reload page. by using useBeforeUnload and useBlocker to check current pathname with next pathname
 * when the user attempts to leave the form. It is meant for preventing unintentional data loss
 * by warning the user before they navigate away from or reload page with the form.

 * Usage: This component must be used within a form context of a form you want to listen.
 * --------------------------------

  <FormProvider {...formContext}>
    <Form onSubmit={handleSubmit(onSubmit)}>
      <TextField />
      <Button variant='outlined'>
        ยกเลิก
      </Button>
      <Button type='submit'>
        สร้างหลักสูตร
      </Button>
    </Form>
    <FormLeavingListener onSubmit={onSubmit}>
  </FormProvider onSubmit={onSubmit}>
 */

export default function FormLeavingListener<TFieldValues extends FieldValues>(
  props: Readonly<FormLeavingListenerProps<TFieldValues>>,
) {
  const { type, onSubmit } = props;
  const formContext = useFormContext<TFieldValues>();
  const { isDirty, isSubmitting, isSubmitSuccessful, disabled: isDisabled } = formContext.formState;
  const { showSnackbar } = useSnackbar();

  // NOTE: This is just a workaround for blocker
  // Issue here: https://github.com/remix-run/react-router/issues/11430#issuecomment-2345829613
  // useRef to check if the component is mounted or not
  const isMountedRef = useRef<boolean>();
  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  const blockerFunction = useCallback<BlockerFunction>(
    ({ currentLocation, nextLocation }) =>
      !!isMountedRef.current &&
      currentLocation.pathname !== nextLocation.pathname &&
      isDirty &&
      !isSubmitSuccessful &&
      !isDisabled,
    [isDirty, isSubmitSuccessful, isDisabled],
  );

  const blocker = useBlocker(blockerFunction);

  const isBlocked = useMemo(
    () => blocker.state === 'blocked' && !isSubmitSuccessful,
    [blocker, isDirty, isDisabled, isSubmitSuccessful],
  );

  useBeforeUnload((event) => {
    // If the form is not dirty, we don't need to prevent the user from leaving.
    if (!isDirty || isSubmitSuccessful || isDisabled) return;
    event.preventDefault();
  });

  const continueToNextPage = () => {
    blocker.proceed?.();
  };

  const cancelNavigation = () => {
    blocker.reset?.();
  };

  const handleSubmit = async () => {
    // Trigger form validation before proceeding
    const isFormInputValid = await formContext.trigger();

    if (!isFormInputValid) {
      showSnackbar({
        message: 'Please complete all required fields',
        variant: 'error',
      });
      blocker.reset?.();
      return;
    }

    if (!type) {
      await onSubmit(formContext.getValues());
      continueToNextPage();
      return;
    }

    formContext.handleSubmit(
      onSubmit(formContext, {
        onCompleted: () => {
          continueToNextPage();
        },
        onError: () => {
          continueToNextPage();
        },
      }),
    );
  };

  useEffect(() => {
    if (isSubmitSuccessful) {
      continueToNextPage();
    }
  }, [isSubmitSuccessful]);

  return (
    <ConfirmationModal
      open={isBlocked}
      loading={isSubmitting}
      title='Exit this page ?'
      content='คุณแน่ใจหรือว่าต้องการออกจากหน้านี้โดยไม่บันทึก?'
      acceptText='Save and Exit'
      discardText='Exit Without Saving'
      onAccept={handleSubmit}
      onDiscard={continueToNextPage}
      onClose={cancelNavigation}
    />
  );
}
